import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileUp, Upload, Trash2, CheckCircle2, Circle, FileText, LibraryBig, Frame, AlertCircle, Loader2, Download } from "lucide-react";
import api, { formatErr } from "../lib/api";

/** ImportsTab — three stacked asset panels (docs, templates, libraries). */
export default function ImportsTab({ projectId }) {
  const [docs, setDocs] = useState([]);
  const [tpls, setTpls] = useState([]);
  const [libs, setLibs] = useState([]);
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, t, l] = await Promise.all([
        api.get(`/projects/${projectId}/documents`),
        api.get(`/projects/${projectId}/templates`),
        api.get(`/projects/${projectId}/symbol_libraries`),
      ]);
      setDocs(d.data); setTpls(t.data); setLibs(l.data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const uploadDoc = async (file) => {
    setBusy((b) => ({ ...b, doc: true })); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/projects/${projectId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy((b) => ({ ...b, doc: false })); }
  };

  const uploadTpl = async (file, name, kind) => {
    setBusy((b) => ({ ...b, tpl: true })); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name || file.name);
      fd.append("kind", kind || "both");
      await api.post(`/projects/${projectId}/templates`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy((b) => ({ ...b, tpl: false })); }
  };

  const uploadLib = async (file) => {
    setBusy((b) => ({ ...b, lib: true })); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/projects/${projectId}/symbol_libraries`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy((b) => ({ ...b, lib: false })); }
  };

  const activate = async (kind, id) => {
    const ep = kind === "doc" ? `/projects/${projectId}/documents/${id}/activate`
             : kind === "tpl" ? `/projects/${projectId}/templates/${id}/activate`
             : `/projects/${projectId}/symbol_libraries/${id}/activate`;
    try { await api.put(ep); await load(); } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };
  const deactivate = async (kind, id) => {
    const ep = kind === "doc" ? `/projects/${projectId}/documents/${id}/deactivate`
             : kind === "tpl" ? `/projects/${projectId}/templates/${id}/deactivate`
             : `/projects/${projectId}/symbol_libraries/${id}/deactivate`;
    try { await api.put(ep); await load(); } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };
  const remove = async (kind, id) => {
    if (!window.confirm("Delete this asset?")) return;
    const ep = kind === "doc" ? `/projects/${projectId}/documents/${id}`
             : kind === "tpl" ? `/projects/${projectId}/templates/${id}`
             : `/projects/${projectId}/symbol_libraries/${id}`;
    try { await api.delete(ep); await load(); } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };

  const openPreview = async (id) => {
    try {
      const { data } = await api.get(`/projects/${projectId}/documents/${id}`);
      setPreviewDoc(data);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="imports-tab">
      {err && <div className="pf-sev pf-sev-error" style={{ display: "block", padding: "8px 12px", marginBottom: 12 }}>{err}</div>}

      <p style={{ color: "var(--pf-text-muted)", fontSize: 13, marginBottom: 16, maxWidth: 960 }}>
        Upload a design basis document, a custom drawing template, or your own symbol library, then mark one of each as <b style={{ color: "var(--pf-accent)" }}>active</b>. The next pipeline run will ground the PDO in your document, draw with your symbols, and frame the PFD/P&amp;ID inside your title block (with ${"{PROJECT_NAME}"} and sibling placeholders auto-filled).
      </p>

      <AssetPanel
        title="Design Documents"
        icon={FileText}
        tk="docs"
        count={docs.length}
        activeCount={docs.filter((d) => d.active).length}
        body={
          <>
            <Dropzone
              accept=".pdf,.docx,.xlsx,.xlsm,.txt,.md,.csv,.html,.json"
              busy={busy.doc}
              label="Drop a design basis document here, or click to browse"
              sub="Accepts PDF · DOCX · XLSX · TXT · MD · CSV · HTML · JSON up to 25 MB"
              onFile={uploadDoc}
              testid="import-doc-drop"
            />
            <AssetList
              items={docs}
              emptyText="No documents yet. Upload a design basis doc to ground the AI output in real engineering data."
              renderRow={(d) => (
                <>
                  <div style={{ minWidth: 0 }}>
                    <div className="pf-mono" style={{ fontSize: 13, color: "var(--pf-text)" }}>{d.filename}</div>
                    <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)" }}>
                      {fmtBytes(d.size_bytes)} · {d.text_len} chars{d.truncated ? " (truncated)" : ""} · {fmtTime(d.created_at)}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn btn-ghost" onClick={() => openPreview(d.id)} data-testid={`doc-preview-${d.id}`}>Preview</button>
                    <ActivateBtn active={d.active} onActivate={() => activate("doc", d.id)} onDeactivate={() => deactivate("doc", d.id)} testid={`doc-toggle-${d.id}`}/>
                    <button className="btn btn-danger" onClick={() => remove("doc", d.id)} data-testid={`doc-del-${d.id}`}><Trash2 size={14}/></button>
                  </div>
                </>
              )}
            />
          </>
        }
      />

      <AssetPanel
        title="Drawing Templates"
        icon={Frame}
        tk="tpls"
        count={tpls.length}
        activeCount={tpls.filter((t) => t.active).length}
        body={
          <>
            <TemplateDrop busy={busy.tpl} onUpload={uploadTpl}/>
            <AssetList
              items={tpls}
              emptyText="No templates yet. Upload an SVG with your title-block and ${PLACEHOLDERS} to brand every generated drawing."
              renderRow={(t) => (
                <>
                  <div style={{ minWidth: 0 }}>
                    <div className="pf-mono" style={{ fontSize: 13 }}>{t.name}</div>
                    <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)" }}>
                      {t.filename} · kind: {t.kind} · {fmtBytes(t.size_bytes)} · {fmtTime(t.created_at)}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <ActivateBtn active={t.active} onActivate={() => activate("tpl", t.id)} onDeactivate={() => deactivate("tpl", t.id)} testid={`tpl-toggle-${t.id}`}/>
                    <button className="btn btn-danger" onClick={() => remove("tpl", t.id)}><Trash2 size={14}/></button>
                  </div>
                </>
              )}
            />
            <div className="pf-card" style={{ padding: 12, marginTop: 10 }}>
              <div className="pf-overline" style={{ marginBottom: 6 }}>PLACEHOLDER REFERENCE</div>
              <div className="pf-mono" style={{ fontSize: 11, color: "var(--pf-text-muted)", lineHeight: 1.7 }}>
                Supported tokens in your template SVG:
                <br/>${"{PROJECT_NAME}"} · ${"{DRAWING_NO}"} · ${"{DRAWING_TITLE}"} · ${"{REV}"} · ${"{DATE}"} · ${"{ENGINEER}"} · ${"{INDUSTRY}"} · ${"{CAPACITY}"} · ${"{UNITS}"}
              </div>
            </div>
          </>
        }
      />

      <AssetPanel
        title="Symbol Libraries"
        icon={LibraryBig}
        tk="libs"
        count={libs.length}
        activeCount={libs.filter((l) => l.active).length}
        body={
          <>
            <Dropzone
              accept=".json"
              busy={busy.lib}
              label="Drop a symbol library JSON here, or click to browse"
              sub='Format: {"name":"...", "symbols":[{"id","name","category","tag_prefix","svg","properties":[…]}, …]}'
              onFile={uploadLib}
              testid="import-lib-drop"
            />
            <AssetList
              items={libs}
              emptyText="No custom libraries. The default ISA 5.1 library is used until you upload your own."
              renderRow={(l) => (
                <>
                  <div style={{ minWidth: 0 }}>
                    <div className="pf-mono" style={{ fontSize: 13 }}>{l.name}</div>
                    <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)" }}>
                      {l.symbol_count} symbols · {fmtBytes(l.size_bytes)} · {fmtTime(l.created_at)}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <ActivateBtn active={l.active} onActivate={() => activate("lib", l.id)} onDeactivate={() => deactivate("lib", l.id)} testid={`lib-toggle-${l.id}`}/>
                    <button className="btn btn-danger" onClick={() => remove("lib", l.id)}><Trash2 size={14}/></button>
                  </div>
                </>
              )}
            />
            <div className="pf-card" style={{ padding: 12, marginTop: 10 }}>
              <div className="pf-overline" style={{ marginBottom: 6 }}>EXAMPLE JSON</div>
              <a
                className="btn btn-ghost"
                href={"data:application/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exampleLibrary(), null, 2))}
                download="rachana-symbols-template.json"
              ><Download size={14}/> Download starter template (JSON)</a>
            </div>
          </>
        }
      />

      {previewDoc && (
        <DocPreview doc={previewDoc} onClose={() => setPreviewDoc(null)}/>
      )}
    </div>
  );
}

function AssetPanel({ title, icon: Icon, count, activeCount, body, tk }) {
  return (
    <div className="pf-card" style={{ padding: 0, marginBottom: 18, overflow: "hidden" }} data-testid={`imports-panel-${tk}`}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--pf-border)", display: "flex", alignItems: "center", gap: 10 }}>
        <Icon size={16} style={{ color: "var(--pf-accent)" }}/>
        <div className="pf-heading" style={{ fontSize: 16 }}>{title}</div>
        <span className="pf-ind-chip">{count} total</span>
        {activeCount > 0 && <span className="pf-ind-chip" style={{ color: "var(--pf-accent)", borderColor: "var(--pf-accent)" }}>{activeCount} active</span>}
      </div>
      <div style={{ padding: 14 }}>{body}</div>
    </div>
  );
}

function Dropzone({ accept, busy, label, sub, onFile, testid }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const onPick = () => ref.current?.click();
  const onDrop = (e) => {
    e.preventDefault(); setOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onFile(f);
  };
  return (
    <div
      onClick={onPick}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      style={{
        border: `1.5px dashed ${over ? "var(--pf-accent)" : "var(--pf-border)"}`,
        background: over ? "rgba(255,176,0,0.05)" : "var(--pf-blueprint)",
        padding: 24, textAlign: "center", cursor: busy ? "wait" : "pointer",
        transition: "background 0.15s, border-color 0.15s",
      }}
      data-testid={testid}
    >
      <input ref={ref} type="file" accept={accept} style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) { onFile(f); e.target.value = ""; } }}/>
      {busy
        ? <><Loader2 className="pf-spin" size={20} style={{ color: "var(--pf-accent)" }}/><div className="pf-mono" style={{ marginTop: 8, color: "var(--pf-accent)" }}>Uploading…</div></>
        : <>
            <FileUp size={22} style={{ color: "var(--pf-accent)" }}/>
            <div className="pf-heading" style={{ fontSize: 14, marginTop: 8 }}>{label}</div>
            <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-muted)", marginTop: 4 }}>{sub}</div>
          </>}
    </div>
  );
}

function TemplateDrop({ busy, onUpload }) {
  const ref = useRef(null);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("both");
  const [over, setOver] = useState(false);

  const trigger = (file) => { if (!file) return; onUpload(file, name || file.name, kind); setName(""); };

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8, marginBottom: 8 }}>
        <input className="pf-input" placeholder="Template name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="tpl-name"/>
        <select className="pf-input" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="tpl-kind">
          <option value="both">Applies to: PFD + P&ID</option>
          <option value="pfd">Applies to: PFD only</option>
          <option value="pid">Applies to: P&ID only</option>
        </select>
      </div>
      <div
        onClick={() => ref.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer?.files?.[0]; if (f) trigger(f); }}
        style={{
          border: `1.5px dashed ${over ? "var(--pf-accent)" : "var(--pf-border)"}`,
          background: over ? "rgba(255,176,0,0.05)" : "var(--pf-blueprint)",
          padding: 24, textAlign: "center", cursor: busy ? "wait" : "pointer",
        }}
        data-testid="import-tpl-drop"
      >
        <input ref={ref} type="file" accept=".svg,image/svg+xml" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) { trigger(f); e.target.value = ""; } }}/>
        {busy
          ? <><Loader2 className="pf-spin" size={20} style={{ color: "var(--pf-accent)" }}/><div className="pf-mono" style={{ marginTop: 8, color: "var(--pf-accent)" }}>Uploading…</div></>
          : <>
              <Frame size={22} style={{ color: "var(--pf-accent)" }}/>
              <div className="pf-heading" style={{ fontSize: 14, marginTop: 8 }}>Drop an SVG drawing template</div>
              <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-muted)", marginTop: 4 }}>
                Include a title-block with ${"{PROJECT_NAME}"}, ${"{REV}"}, ${"{DATE}"}… placeholders
              </div>
            </>}
      </div>
    </>
  );
}

function AssetList({ items, renderRow, emptyText }) {
  if (!items || items.length === 0) {
    return <div className="pf-card" style={{ padding: 18, color: "var(--pf-text-muted)", fontSize: 13, marginTop: 12 }}>{emptyText}</div>;
  }
  return (
    <ul style={{ listStyle: "none", margin: "12px 0 0", padding: 0 }}>
      {items.map((it) => (
        <li key={it.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", gap: 12, border: `1px solid ${it.active ? "var(--pf-accent)" : "var(--pf-border)"}`, background: it.active ? "rgba(255,176,0,0.05)" : "var(--pf-surface-2)", marginBottom: 6 }}>
          {renderRow(it)}
        </li>
      ))}
    </ul>
  );
}

function ActivateBtn({ active, onActivate, onDeactivate, testid }) {
  return active
    ? <button className="btn btn-ghost" onClick={onDeactivate} style={{ borderColor: "var(--pf-accent)", color: "var(--pf-accent)" }} data-testid={testid}><CheckCircle2 size={14}/> Active</button>
    : <button className="btn btn-ghost" onClick={onActivate} data-testid={testid}><Circle size={14}/> Activate</button>;
}

function DocPreview({ doc, onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 60 }}/>
      <aside className="pf-grain fade-in" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(720px, 96vw)", background: "var(--pf-surface)", borderLeft: "1px solid var(--pf-border)", zIndex: 70, display: "flex", flexDirection: "column" }}>
        <header style={{ padding: 14, borderBottom: "1px solid var(--pf-border)", display: "flex", gap: 10, alignItems: "center" }}>
          <FileText size={16} style={{ color: "var(--pf-accent)" }}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pf-overline">DOCUMENT · EXTRACTED TEXT</div>
            <div className="pf-heading" style={{ fontSize: 16 }}>{doc.filename}</div>
          </div>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </header>
        <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
          <pre className="pf-code" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{doc.text}</pre>
        </div>
      </aside>
    </>
  );
}

// ---- helpers ----
function fmtBytes(n) { if (n == null) return "—"; if (n < 1024) return n + " B"; if (n < 1024*1024) return (n/1024).toFixed(1) + " KB"; return (n/1024/1024).toFixed(1) + " MB"; }
function fmtTime(t) { try { return new Date(t).toLocaleString(); } catch { return t; } }

function exampleLibrary() {
  return {
    name: "ACME Corp Standard Symbols",
    version: "1.0",
    symbols: [
      {
        id: "acme_centrifugal_pump",
        name: "ACME Centrifugal Pump",
        category: "Rotating Equipment",
        tag_prefix: "P",
        standards: ["ACME-STD-PMP-001", "API 610"],
        svg: '<circle cx="40" cy="40" r="22" fill="none" stroke="#F3F4F6" stroke-width="1.5"/><polygon points="26,28 26,52 52,40" fill="#F3F4F6"/><text x="40" y="70" text-anchor="middle" font-family="JetBrains Mono" font-size="8" fill="#FFB000">ACME</text>',
        properties: ["design_pressure_bar", "flow_m3_h", "head_m", "motor_kw", "npsh_m"],
      },
      {
        id: "acme_vertical_tank",
        name: "ACME Vertical Tank",
        category: "Vessels & Tanks",
        tag_prefix: "T",
        standards: ["ACME-STD-VES-002", "API 650"],
        svg: '<rect x="22" y="10" width="36" height="62" fill="none" stroke="#F3F4F6" stroke-width="1.5"/><ellipse cx="40" cy="10" rx="18" ry="4" fill="none" stroke="#F3F4F6" stroke-width="1.5"/>',
        properties: ["volume_m3", "material_of_construction", "design_pressure_bar", "design_temperature_c"],
      },
    ],
  };
}
